import { LogService } from 'services/data-services/LogService';
import type { AxiosInstance } from 'axios';
import { Result } from 'services/Result';
import { injectable } from 'inversify';
import { ITaggingProvider } from 'interfaces/providers/ITaggingProvider';
import { IApiUpdateSequenceModel } from 'interfaces/tagging/ApiModels/IApiUpdateSequenceModel';
import { IApiStadiumPlayModel } from 'interfaces/tagging/ApiModels/IApiStadiumPlayModel';
import { ContextStore } from 'stores/ContextStore';
import { IApiPlaysWithBallFinals } from '../../interfaces/session/IApiPlaysWithBallFinals';

@injectable()
export class TaggingService implements ITaggingProvider {
  private axiosInstance: AxiosInstance;

  private contextStore: ContextStore;

  private logService: LogService;

  constructor(axiosInstance: AxiosInstance, contextStore: ContextStore, logService: LogService) {
    this.axiosInstance = axiosInstance;
    this.contextStore = contextStore;
    this.logService = logService;
  }

  private checkForInvalidPlays = (play: IApiStadiumPlayModel, fnName: string): void => {
    try {
      if (play.taggerBehavior.manuallyAddedByTagger === false && play.playId === null) {
        this.logService.logError('Missing playId and it is not created manually by tagger.', {
          play,
          fnName,
        });
      }
    } catch (error: any) {
      this.logService.logError('Error when validating play.', {
        error,
        play,
        fnName,
      });
    }
  };

  async storePlay(play: IApiStadiumPlayModel, fnName: string) {
    this.checkForInvalidPlays(play, fnName);

    const response = await this.axiosInstance.post<Result<string>>('api/play/savePlay/', play);
    if (response.status !== 200) {
      throw new Error(`Error storing play: ${response.status}`);
    }
  }

  async storePlays(plays: IApiStadiumPlayModel[], fnName: string) {
    plays.forEach((play) => {
      this.checkForInvalidPlays(play, fnName);
    });

    const response = await this.axiosInstance.post<Result<string>>('api/play/savePlays/', plays);
    if (response.status !== 200) {
      throw new Error(`Error storing plays: ${response.status}`);
    }
  }

  async getSessionPlays(sessionId: string) {
    const response = await this.axiosInstance.get<Result<IApiStadiumPlayModel[]>>(`api/play/session/${sessionId}`);
    if (response.data && !response.data.isError && response.data.data) {
      const r = response.data.data as IApiStadiumPlayModel[];
      return r;
    }
    throw new Error(`Error getting session plays: ${response.status}`);
  }

  async getPlaysWithBallFinals(sessionId: string) {
    const response = await this.axiosInstance.get<Result<IApiPlaysWithBallFinals>>(`api/play/session/${sessionId}/withBallFinals`);
    if (response.data && !response.data.isError && response.data.data) {
      const r = response.data.data as IApiPlaysWithBallFinals;
      return r;
    }
    throw new Error(`Error getting session plays: ${response.status}`);
  }

  async updateSequenceNumbers(model: IApiUpdateSequenceModel) {
    const response = await this.axiosInstance.post<Result<string>>('api/play/updatePlaySequence/', model);
    if (response.status !== 200) {
      throw new Error(`Error updating sequence numbers: ${response.status}`);
    }
  }
}
